import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from './types';

// ============================================
// HOUSE STARK & ALLIES
// ============================================

export function JonSnowModel({ world, hovered }: NPCModelProps) {
  const cloakColor = world === 'real' ? '#1a1a1a' : '#0a0a1a';
  const skinColor = world === 'real' ? '#e0c8b0' : '#c0a890';
  const hairColor = '#1a1a1a';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Night's Watch black cloak/body */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.25, 0.8, 8]} />
        <meshStandardMaterial
          color={cloakColor}
          roughness={0.9}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Cloak collar / fur trim */}
      <mesh position={[0, 0.75, 0]} castShadow>
        <torusGeometry args={[0.15, 0.05, 8, 16]} />
        <meshStandardMaterial color={world === 'real' ? '#3a3a3a' : '#2a2a3a'} roughness={1} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.7} />
      </mesh>
      {/* Curly black hair */}
      <mesh position={[0, 1.02, -0.02]} castShadow>
        <sphereGeometry args={[0.13, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.6]} />
        <meshStandardMaterial color={hairColor} roughness={0.95} />
      </mesh>
      {/* Hair sides */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.95, -0.05]} castShadow>
          <sphereGeometry args={[0.06, 6, 6]} />
          <meshStandardMaterial color={hairColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Beard/stubble - darker face area */}
      <mesh position={[0, 0.88, 0.08]}>
        <sphereGeometry args={[0.08, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#c8b098' : '#a89078'} roughness={0.8} />
      </mesh>
      {/* Eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.97, 0.1]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#4a3a2a" />
        </mesh>
      ))}
      {/* Longclaw sword on back */}
      <mesh position={[0.1, 0.5, -0.15]} rotation={[0.2, 0, 0.1]} castShadow>
        <boxGeometry args={[0.03, 0.6, 0.01]} />
        <meshStandardMaterial color="#a0a0a0" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Sword handle */}
      <mesh position={[0.1, 0.85, -0.15]} castShadow>
        <cylinderGeometry args={[0.015, 0.015, 0.1, 6]} />
        <meshStandardMaterial color="#3a3a3a" />
      </mesh>
      {/* Wolf pommel */}
      <mesh position={[0.1, 0.92, -0.15]} castShadow>
        <sphereGeometry args={[0.025, 6, 6]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.7} />
      </mesh>
    </group>
  );
}

export function AryaStarkModel({ world, hovered }: NPCModelProps) {
  const clothColor = world === 'real' ? '#4a4a4a' : '#3a3a4a';
  const skinColor = world === 'real' ? '#e8d0b8' : '#c8b098';
  const hairColor = '#3a2a1a';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Slim body - practical clothes */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.7, 8]} />
        <meshStandardMaterial
          color={clothColor}
          roughness={0.85}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Leather vest */}
      <mesh position={[0, 0.45, 0.05]} castShadow>
        <boxGeometry args={[0.18, 0.35, 0.08]} />
        <meshStandardMaterial color={world === 'real' ? '#5a4a3a' : '#4a3a3a'} roughness={0.8} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.7} />
      </mesh>
      {/* Short hair / boyish cut */}
      <mesh position={[0, 0.86, -0.01]} castShadow>
        <sphereGeometry args={[0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.55]} />
        <meshStandardMaterial color={hairColor} roughness={0.9} />
      </mesh>
      {/* Eyes - intense */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.82, 0.08]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshBasicMaterial color="#5a5a5a" />
        </mesh>
      ))}
      {/* Needle sword at hip */}
      <mesh position={[0.12, 0.25, 0]} rotation={[0, 0, 0.15]} castShadow>
        <boxGeometry args={[0.015, 0.35, 0.008]} />
        <meshStandardMaterial color="#b0b0b0" metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Belt */}
      <mesh position={[0, 0.15, 0]}>
        <torusGeometry args={[0.12, 0.015, 8, 16]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.8} />
      </mesh>
    </group>
  );
}

export function SansaStarkModel({ world, hovered }: NPCModelProps) {
  const dressColor = world === 'real' ? '#4a6a8a' : '#3a4a6a';
  const skinColor = world === 'real' ? '#f0d8c0' : '#d0b8a0';
  const hairColor = world === 'real' ? '#8B4513' : '#6a3a2a';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Elegant dress */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <coneGeometry args={[0.25, 0.8, 8]} />
        <meshStandardMaterial
          color={dressColor}
          roughness={0.6}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Bodice */}
      <mesh position={[0, 0.7, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.25, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#3a5a7a' : '#2a3a5a'} roughness={0.5} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <sphereGeometry args={[0.11, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.6} />
      </mesh>
      {/* Long auburn hair */}
      <mesh position={[0, 0.95, -0.05]} castShadow>
        <capsuleGeometry args={[0.1, 0.3, 8, 12]} />
        <meshStandardMaterial color={hairColor} roughness={0.85} />
      </mesh>
      {/* Hair front */}
      <mesh position={[0, 1.02, 0]} castShadow>
        <sphereGeometry args={[0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.5]} />
        <meshStandardMaterial color={hairColor} roughness={0.85} />
      </mesh>
      {/* Eyes */}
      {[-0.035, 0.035].map((x, i) => (
        <mesh key={i} position={[x, 0.97, 0.09]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#4169E1" />
        </mesh>
      ))}
      {/* Necklace */}
      <mesh position={[0, 0.82, 0.08]}>
        <torusGeometry args={[0.04, 0.008, 8, 16]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.7} />
      </mesh>
    </group>
  );
}

export function BranStarkModel({ world, hovered }: NPCModelProps) {
  const clothColor = world === 'real' ? '#5a5a5a' : '#3a3a4a';
  const skinColor = world === 'real' ? '#e8d0b8' : '#c8b098';
  const hairColor = '#4a3a2a';
  const highlightColor = hovered ? '#FFD700' : undefined;
  const eyeColor = world === 'spectral' ? '#FFFFFF' : '#4a3a2a';

  return (
    <group>
      {/* Seated/wheelchair position - body */}
      <mesh position={[0, 0.3, 0]} castShadow>
        <boxGeometry args={[0.25, 0.3, 0.2]} />
        <meshStandardMaterial
          color={clothColor}
          roughness={0.8}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Wheelchair frame */}
      <mesh position={[0, 0.2, -0.15]} castShadow>
        <boxGeometry args={[0.3, 0.4, 0.05]} />
        <meshStandardMaterial color="#3a3a3a" roughness={0.7} />
      </mesh>
      {/* Wheels */}
      {[-0.18, 0.18].map((x, i) => (
        <mesh key={i} position={[x, 0.15, -0.1]} rotation={[0, Math.PI/2, 0]} castShadow>
          <torusGeometry args={[0.15, 0.02, 8, 16]} />
          <meshStandardMaterial color="#2a2a2a" roughness={0.6} />
        </mesh>
      ))}
      {/* Head */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.7} />
      </mesh>
      {/* Hair */}
      <mesh position={[0, 0.66, -0.02]} castShadow>
        <sphereGeometry args={[0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.55]} />
        <meshStandardMaterial color={hairColor} roughness={0.9} />
      </mesh>
      {/* Eyes - white when warging */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.62, 0.08]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshStandardMaterial
            color={eyeColor}
            emissive={world === 'spectral' ? '#FFFFFF' : undefined}
            emissiveIntensity={world === 'spectral' ? 0.5 : 0}
          />
        </mesh>
      ))}
      {/* Three-eyed raven aura in spectral */}
      {world === 'spectral' && (
        <>
          <mesh position={[0, 0.8, 0]}>
            <sphereGeometry args={[0.25, 12, 12]} />
            <meshStandardMaterial color="#4a6a8a" transparent opacity={0.2} emissive="#4a8aff" emissiveIntensity={0.3} />
          </mesh>
          <pointLight position={[0, 0.6, 0]} color="#4a8aff" intensity={2} distance={2} />
        </>
      )}
    </group>
  );
}

export function DirewolfModel({ world, hovered }: NPCModelProps) {
  const furColor = world === 'real' ? '#f8f8f8' : '#d0d0e0';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Body - large wolf */}
      <mesh position={[0, 0.35, 0]} rotation={[0, 0, 0]} castShadow>
        <capsuleGeometry args={[0.2, 0.5, 8, 16]} />
        <meshStandardMaterial
          color={furColor}
          roughness={0.95}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.4, 0.35]} castShadow>
        <boxGeometry args={[0.2, 0.18, 0.25]} />
        <meshStandardMaterial color={furColor} roughness={0.95} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.35, 0.5]} castShadow>
        <boxGeometry args={[0.1, 0.1, 0.15]} />
        <meshStandardMaterial color={furColor} roughness={0.95} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.37, 0.58]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Ears */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 0.55, 0.3]} castShadow>
          <coneGeometry args={[0.05, 0.12, 4]} />
          <meshStandardMaterial color={furColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Eyes - red in Ghost */}
      {[-0.06, 0.06].map((x, i) => (
        <mesh key={i} position={[x, 0.42, 0.45]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshStandardMaterial
            color={world === 'real' ? '#8B0000' : '#aa2a2a'}
            emissive="#8B0000"
            emissiveIntensity={0.5}
          />
        </mesh>
      ))}
      {/* Legs */}
      {[[-0.12, 0.15], [0.12, 0.15], [-0.12, -0.15], [0.12, -0.15]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.15, z]} castShadow>
          <cylinderGeometry args={[0.04, 0.05, 0.3, 6]} />
          <meshStandardMaterial color={furColor} roughness={0.95} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.4, -0.35]} rotation={[0.5, 0, 0]} castShadow>
        <capsuleGeometry args={[0.04, 0.25, 6, 8]} />
        <meshStandardMaterial color={furColor} roughness={0.95} />
      </mesh>
    </group>
  );
}

export function TyrionLannisterModel({ world, hovered }: NPCModelProps) {
  const clothColor = world === 'real' ? '#8B0000' : '#5a2a2a';
  const skinColor = world === 'real' ? '#e8d0b8' : '#c8b098';
  const hairColor = '#c0a050';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Short stature body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.5, 8]} />
        <meshStandardMaterial
          color={clothColor}
          roughness={0.7}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Gold trim on clothes */}
      <mesh position={[0, 0.35, 0.08]}>
        <boxGeometry args={[0.04, 0.3, 0.02]} />
        <meshStandardMaterial color="#c0a050" metalness={0.5} roughness={0.4} />
      </mesh>
      {/* Head - proportionally larger */}
      <mesh position={[0, 0.6, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.7} />
      </mesh>
      {/* Blonde hair */}
      <mesh position={[0, 0.68, -0.02]} castShadow>
        <sphereGeometry args={[0.12, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.55]} />
        <meshStandardMaterial color={hairColor} roughness={0.85} />
      </mesh>
      {/* Beard */}
      <mesh position={[0, 0.52, 0.08]}>
        <sphereGeometry args={[0.06, 6, 6]} />
        <meshStandardMaterial color={hairColor} roughness={0.9} />
      </mesh>
      {/* Eyes - intelligent */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.62, 0.1]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#228B22" />
        </mesh>
      ))}
      {/* Wine goblet */}
      <mesh position={[0.15, 0.35, 0.05]} castShadow>
        <cylinderGeometry args={[0.03, 0.02, 0.08, 8]} />
        <meshStandardMaterial color="#c0a050" metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Wine */}
      <mesh position={[0.15, 0.38, 0.05]}>
        <cylinderGeometry args={[0.025, 0.025, 0.02, 8]} />
        <meshStandardMaterial color="#722F37" />
      </mesh>
    </group>
  );
}

export function DaenerysModel({ world, hovered }: NPCModelProps) {
  const dressColor = world === 'real' ? '#f0f0f0' : '#d0d0e0';
  const skinColor = world === 'real' ? '#f8e8d8' : '#d8c8b8';
  const hairColor = '#f0f0f0';
  const highlightColor = hovered ? '#FFD700' : undefined;

  return (
    <group>
      {/* Elegant flowing dress */}
      <mesh position={[0, 0.4, 0]} castShadow>
        <coneGeometry args={[0.25, 0.8, 8]} />
        <meshStandardMaterial
          color={dressColor}
          roughness={0.4}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Bodice */}
      <mesh position={[0, 0.7, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.25, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#e8e8e8' : '#c8c8d8'} roughness={0.3} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.95, 0]} castShadow>
        <sphereGeometry args={[0.11, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.6} />
      </mesh>
      {/* Silver-white braided hair */}
      <mesh position={[0, 0.95, -0.06]} castShadow>
        <capsuleGeometry args={[0.1, 0.35, 8, 12]} />
        <meshStandardMaterial color={hairColor} roughness={0.7} />
      </mesh>
      {/* Hair braids on top */}
      <mesh position={[0, 1.05, 0]} castShadow>
        <torusGeometry args={[0.08, 0.03, 8, 16]} />
        <meshStandardMaterial color={hairColor} roughness={0.7} />
      </mesh>
      {/* Eyes - violet */}
      {[-0.035, 0.035].map((x, i) => (
        <mesh key={i} position={[x, 0.97, 0.09]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshStandardMaterial
            color="#8A2BE2"
            emissive="#8A2BE2"
            emissiveIntensity={0.3}
          />
        </mesh>
      ))}
      {/* Dragon brooch */}
      <mesh position={[0, 0.8, 0.1]}>
        <octahedronGeometry args={[0.03]} />
        <meshStandardMaterial color="#c0a050" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

// ============================================
// WHITE WALKERS & ARMY OF THE DEAD
// ============================================

export function NightKingModel({ world, hovered }: NPCModelProps) {
  const skinColor = world === 'spectral' ? '#4a6a8a' : '#6a8aa0';
  const highlightColor = hovered ? '#4a8aff' : undefined;

  return (
    <group>
      {/* Armored body */}
      <mesh position={[0, 0.5, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.25, 1, 8]} />
        <meshStandardMaterial
          color={skinColor}
          roughness={0.4}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Ice armor plates */}
      {[0, Math.PI/2, Math.PI, Math.PI*1.5].map((angle, i) => (
        <mesh key={i} position={[Math.cos(angle) * 0.17, 0.5, Math.sin(angle) * 0.17]} castShadow>
          <boxGeometry args={[0.08, 0.3, 0.03]} />
          <meshStandardMaterial
            color="#6aaaff"
            roughness={0.1}
            transparent
            opacity={0.8}
            emissive="#4a8aff"
            emissiveIntensity={0.3}
          />
        </mesh>
      ))}
      {/* Head with ice crown */}
      <mesh position={[0, 1.1, 0]} castShadow>
        <sphereGeometry args={[0.15, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.5} />
      </mesh>
      {/* Ice crown spikes */}
      {[0, 1, 2, 3, 4].map((i) => {
        const angle = (i / 5) * Math.PI * 2;
        return (
          <mesh key={i} position={[Math.cos(angle) * 0.12, 1.25, Math.sin(angle) * 0.12]} castShadow>
            <coneGeometry args={[0.03, 0.15 + Math.abs(Math.sin(i)) * 0.1, 5]} />
            <meshStandardMaterial
              color="#6aaaff"
              roughness={0.1}
              transparent
              opacity={0.85}
              emissive="#4a8aff"
              emissiveIntensity={0.5}
            />
          </mesh>
        );
      })}
      {/* Glowing blue eyes */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 1.12, 0.12]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshStandardMaterial
            color="#00BFFF"
            emissive="#00BFFF"
            emissiveIntensity={1.5}
          />
        </mesh>
      ))}
      {/* Ice spear */}
      <mesh position={[0.25, 0.7, 0]} rotation={[0, 0, 0.2]} castShadow>
        <cylinderGeometry args={[0.02, 0.02, 1.5, 6]} />
        <meshStandardMaterial
          color="#6aaaff"
          roughness={0.1}
          transparent
          opacity={0.9}
          emissive="#4a8aff"
          emissiveIntensity={0.3}
        />
      </mesh>
      <mesh position={[0.25, 1.45, 0]} rotation={[0, 0, 0.2]} castShadow>
        <coneGeometry args={[0.05, 0.2, 4]} />
        <meshStandardMaterial
          color="#8acaff"
          roughness={0.1}
          emissive="#4a8aff"
          emissiveIntensity={0.5}
        />
      </mesh>
      {/* Ambient glow */}
      <pointLight position={[0, 1, 0]} color="#4a8aff" intensity={3} distance={4} />
    </group>
  );
}

export function WhiteWalkerModel({ world, hovered }: NPCModelProps) {
  const skinColor = world === 'spectral' ? '#5a7a9a' : '#7a9ab0';
  const highlightColor = hovered ? '#4a8aff' : undefined;

  return (
    <group>
      {/* Skeletal body */}
      <mesh position={[0, 0.45, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.18, 0.9, 8]} />
        <meshStandardMaterial
          color={skinColor}
          roughness={0.5}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Ice armor */}
      <mesh position={[0, 0.5, 0.1]} castShadow>
        <boxGeometry args={[0.2, 0.4, 0.05]} />
        <meshStandardMaterial
          color="#6aaaff"
          roughness={0.15}
          transparent
          opacity={0.7}
          emissive="#4a8aff"
          emissiveIntensity={0.2}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 1, 0]} castShadow>
        <sphereGeometry args={[0.12, 12, 12]} />
        <meshStandardMaterial color={skinColor} roughness={0.5} />
      </mesh>
      {/* Elongated skull features */}
      <mesh position={[0, 1.08, -0.03]} castShadow>
        <sphereGeometry args={[0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.5]} />
        <meshStandardMaterial color={skinColor} roughness={0.5} />
      </mesh>
      {/* Glowing blue eyes */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 1.02, 0.1]}>
          <sphereGeometry args={[0.02, 8, 8]} />
          <meshStandardMaterial
            color="#00BFFF"
            emissive="#00BFFF"
            emissiveIntensity={1.2}
          />
        </mesh>
      ))}
      {/* Ice sword */}
      <mesh position={[0.18, 0.5, 0]} rotation={[0, 0, 0.1]} castShadow>
        <boxGeometry args={[0.03, 0.7, 0.01]} />
        <meshStandardMaterial
          color="#8acaff"
          roughness={0.1}
          transparent
          opacity={0.85}
          emissive="#4a8aff"
          emissiveIntensity={0.4}
        />
      </mesh>
      <pointLight position={[0, 0.8, 0]} color="#4a8aff" intensity={1.5} distance={2} />
    </group>
  );
}

export function WightModel({ world, hovered }: NPCModelProps) {
  const skinColor = world === 'spectral' ? '#4a4a5a' : '#5a5a6a';
  const highlightColor = hovered ? '#4a8aff' : undefined;

  return (
    <group>
      {/* Decayed body */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.15, 0.7, 6]} />
        <meshStandardMaterial
          color={skinColor}
          roughness={0.95}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Tattered clothing */}
      <mesh position={[0, 0.3, 0.05]} castShadow>
        <boxGeometry args={[0.15, 0.3, 0.05]} />
        <meshStandardMaterial color="#2a2a2a" roughness={1} />
      </mesh>
      {/* Head - skeletal */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <sphereGeometry args={[0.1, 10, 10]} />
        <meshStandardMaterial color={skinColor} roughness={0.9} />
      </mesh>
      {/* Hollow eye sockets with blue glow */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.82, 0.08]}>
          <sphereGeometry args={[0.02, 6, 6]} />
          <meshStandardMaterial
            color="#00BFFF"
            emissive="#00BFFF"
            emissiveIntensity={0.8}
          />
        </mesh>
      ))}
      {/* Reaching arm */}
      <mesh position={[0.15, 0.5, 0.1]} rotation={[0.5, 0, 0.3]} castShadow>
        <capsuleGeometry args={[0.025, 0.25, 4, 8]} />
        <meshStandardMaterial color={skinColor} roughness={0.95} />
      </mesh>
      {/* Bony hand */}
      <mesh position={[0.22, 0.6, 0.2]}>
        <sphereGeometry args={[0.03, 6, 6]} />
        <meshStandardMaterial color={skinColor} roughness={0.95} />
      </mesh>
    </group>
  );
}

export function WightGiantModel({ world, hovered }: NPCModelProps) {
  const skinColor = world === 'spectral' ? '#4a5a6a' : '#5a6a7a';
  const highlightColor = hovered ? '#4a8aff' : undefined;

  return (
    <group>
      {/* Massive body */}
      <mesh position={[0, 0.8, 0]} castShadow>
        <capsuleGeometry args={[0.4, 0.8, 8, 16]} />
        <meshStandardMaterial
          color={skinColor}
          roughness={0.9}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </mesh>
      {/* Frost patches */}
      {[[0.2, 0.9, 0.3], [-0.25, 0.7, 0.25], [0.1, 1.1, -0.2]].map(([x, y, z], i) => (
        <mesh key={i} position={[x, y, z]} castShadow>
          <sphereGeometry args={[0.15, 6, 6]} />
          <meshStandardMaterial
            color="#6aaaff"
            roughness={0.2}
            transparent
            opacity={0.5}
            emissive="#4a8aff"
            emissiveIntensity={0.3}
          />
        </mesh>
      ))}
      {/* Head */}
      <mesh position={[0, 1.5, 0]} castShadow>
        <sphereGeometry args={[0.25, 10, 10]} />
        <meshStandardMaterial color={skinColor} roughness={0.9} />
      </mesh>
      {/* Glowing eyes */}
      {[-0.08, 0.08].map((x, i) => (
        <mesh key={i} position={[x, 1.52, 0.2]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial
            color="#00BFFF"
            emissive="#00BFFF"
            emissiveIntensity={1}
          />
        </mesh>
      ))}
      {/* Arms */}
      {[-1, 1].map((side, i) => (
        <mesh key={i} position={[side * 0.5, 0.8, 0]} rotation={[0, 0, side * 0.3]} castShadow>
          <capsuleGeometry args={[0.12, 0.5, 6, 8]} />
          <meshStandardMaterial color={skinColor} roughness={0.9} />
        </mesh>
      ))}
      {/* Legs */}
      {[-0.2, 0.2].map((x, i) => (
        <mesh key={i} position={[x, 0.25, 0]} castShadow>
          <cylinderGeometry args={[0.15, 0.18, 0.5, 6]} />
          <meshStandardMaterial color={skinColor} roughness={0.9} />
        </mesh>
      ))}
      <pointLight position={[0, 1.2, 0]} color="#4a8aff" intensity={2} distance={3} />
    </group>
  );
}

// Registry of all Game of Thrones character models
export const gameOfThronesNPCs: NPCModelRegistry = {
  // House Stark & Allies
  jon_snow: JonSnowModel,
  arya_stark: AryaStarkModel,
  sansa_stark: SansaStarkModel,
  bran_stark: BranStarkModel,
  direwolf: DirewolfModel,
  tyrion_lannister: TyrionLannisterModel,
  daenerys: DaenerysModel,
  // Army of the Dead
  night_king: NightKingModel,
  white_walker: WhiteWalkerModel,
  wight: WightModel,
  wight_giant: WightGiantModel,
};
